import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { ClipboardList } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { coachesService, type CoachSummary } from '../services/coaches';

/**
 * Public landing reached through a coach's secret invite link. Shows who the
 * athlete is about to send a plan to, then launches the plan questionnaire
 * pre-filled with that coach (after sign-in if needed).
 */
export default function CoachInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();

  const [coach, setCoach] = useState<CoachSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!token) return;
    let active = true;
    setLoading(true);
    coachesService
      .get(token)
      .then((c) => active && setCoach(c))
      .catch(() => active && setNotFound(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  const coachName = coach ? coach.full_name || coach.username : '';

  function handleSendPlan() {
    if (!coach || !token) return;
    if (!isAuthenticated) {
      navigate('/sign-in', { state: { from: `/coach/${token}` } });
      return;
    }
    navigate('/training-plans/new', {
      state: {
        coach: { id: coach.id, username: coach.username, name: coach.full_name },
        coachToken: token,
      },
    });
  }

  return (
    <div className="max-w-md mx-auto px-4 py-12">
      <Card>
        <CardHeader className="items-center text-center">
          {loading ? (
            <>
              <Skeleton className="h-20 w-20 rounded-full" />
              <Skeleton className="h-5 w-40" />
            </>
          ) : notFound ? (
            <>
              <CardTitle>{t('coachInvite.invalidTitle')}</CardTitle>
              <CardDescription>{t('coachInvite.invalidBody')}</CardDescription>
            </>
          ) : (
            <>
              <Avatar className="h-20 w-20">
                <AvatarImage src={coach?.avatar_medium_url ?? undefined} alt={coachName} />
                <AvatarFallback>{coachName.slice(0, 2).toUpperCase()}</AvatarFallback>
              </Avatar>
              <CardTitle className="text-xl">{t('coachInvite.title', { name: coachName })}</CardTitle>
              <CardDescription>{t('coachInvite.subtitle', { name: coachName })}</CardDescription>
            </>
          )}
        </CardHeader>
        {!loading && !notFound && (
          <CardContent>
            <Button className="w-full" onClick={handleSendPlan}>
              <ClipboardList className="h-4 w-4 mr-2" />
              {isAuthenticated ? t('coachInvite.sendPlan') : t('coachInvite.signInToSend')}
            </Button>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
